import { ArrowRightOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Button } from "antd"
import {
  ChevronDown,
  ExternalLink,
  Handshake,
  Mail,
  MessageCircle,
  Presentation
} from "lucide-react"
import { useState } from "react"

import syncIcon from "~/assets/sync-icon.png"
import MarkdownRender from "~/components/markdown-render"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { toPlainStringForApi } from "~/utils/location-helpers"

import { EVENTS } from "../knowledge-os/pre-meeting/constants"

// Accent colors cycling for talking points: purple → blue → teal → coral
const TALKING_POINT_COLORS = [
  {
    hex: "#6c5ce7",
    barStart: "rgba(108,92,231,0.85)",
    barEnd: "rgba(108,92,231,0.35)"
  },
  {
    hex: "#0984e3",
    barStart: "rgba(9,132,227,0.85)",
    barEnd: "rgba(9,132,227,0.35)"
  },
  {
    hex: "#00b894",
    barStart: "rgba(0,184,148,0.85)",
    barEnd: "rgba(0,184,148,0.35)"
  },
  {
    hex: "#e17055",
    barStart: "rgba(225,112,85,0.85)",
    barEnd: "rgba(225,112,85,0.35)"
  }
]

const GUIDELINE_CONFIG: Record<
  string,
  { label: string; hex: string; bg: string; shadow: string }
> = {
  first_meeting: {
    label: "person_brief.label_first_meeting",
    hex: "#00b894",
    bg: "rgba(0,184,148,0.10)",
    shadow: "0 1px 3px rgba(0,184,148,0.15)"
  },
  writing_the_email: {
    label: "person_brief.label_writing_the_email",
    hex: "#0984e3",
    bg: "rgba(9,132,227,0.10)",
    shadow: "0 1px 3px rgba(9,132,227,0.15)"
  },
  commercial_negotiations: {
    label: "person_brief.label_commercial_negotiations",
    hex: "#e17055",
    bg: "rgba(225,112,85,0.10)",
    shadow: "0 1px 3px rgba(225,112,85,0.15)"
  },
  content_related_discussions: {
    label: "person_brief.label_content_related_discussions",
    hex: "#6c5ce7",
    bg: "rgba(108,92,231,0.10)",
    shadow: "0 1px 3px rgba(108,92,231,0.15)"
  }
}

function getGuidelineIcon(key: string) {
  switch (key) {
    case "first_meeting":
      return <Handshake size={14} />
    case "writing_the_email":
      return <Mail size={14} />
    case "commercial_negotiations":
      return <Presentation size={14} />
    case "content_related_discussions":
      return <MessageCircle size={14} />
    default:
      return null
  }
}

function briefText(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "object" && !Array.isArray(value)) {
    return toPlainStringForApi(value as Record<string, unknown>)
  }
  return String(value)
}

const priorityMarkdownStyles = `
.priority-markdown .markdown ul,
.priority-markdown .markdown ol {
  list-style: none !important;
  padding-left: 0 !important;
  margin: 0 !important;
}
.priority-markdown .markdown ul li,
.priority-markdown .markdown ol li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 3px 0 !important;
  line-height: 1.6;
}
.priority-markdown .markdown ul li::before {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #6c5ce7;
  flex-shrink: 0;
  margin-top: 6px;
}
.priority-markdown .markdown p {
  margin: 0 0 4px !important;
}
`

export const Section = ({
  label: _label,
  data,
  type,
  id
}: {
  label: string
  data: any
  type: string
  id?: string
}) => {
  const { t } = useTranslation()
  const [expandedPriorities, setExpandedPriorities] = useState<
    Record<number, boolean>
  >({})
  const [showAllNews, setShowAllNews] = useState(false)

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date
      .toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
      })
      .replace(/ (\d{4})$/, ", $1")
  }

  if (!data || (Array.isArray(data) && data.length === 0) || data === "NA") {
    return null
  }

  // --- Talking points ---
  if (type === "talking_points") {
    return (
      <div id={id} data-testid={id} className="flex flex-col gap-2.5">
        {(data as any[]).map((item: any, index: number) => {
          const c = TALKING_POINT_COLORS[index % TALKING_POINT_COLORS.length]
          const tags = briefText(item.rationale_string)
            .trim()
            .split(", ")
            .filter(Boolean)
          return (
            <div
              key={index}
              className="relative flex rounded-xl border-[0.5px] border-light-border dark:border-border bg-white dark:bg-dark-2 overflow-hidden transition-all hover:border-primary/25 dark:hover:border-primary-light/25 hover:shadow-[0_2px_8px_rgba(108,92,231,0.06)]"
            >
              <div
                className="w-[3px] flex-shrink-0"
                style={{
                  background: `linear-gradient(to bottom, ${c.barStart}, ${c.barEnd})`
                }}
              />
              <div className="flex flex-col gap-2 p-4 flex-1">
                <div className="flex items-center gap-2.5">
                  <span
                    className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[11px] font-semibold flex-shrink-0"
                    style={{ background: c.hex }}
                  >
                    {index + 1}
                  </span>
                  <p className="text-[13px] font-semibold text-dark-2 dark:text-dark-6">
                    {briefText(item.title)}
                  </p>
                </div>
                <p className="text-13 leading-[1.65] text-dark-2 dark:text-dark-6 ml-[34px]">
                  {briefText(item.description)}
                </p>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 ml-[34px]">
                    {tags.map((tag: string, i: number) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-genesis dark:bg-dark-1/40 border-[0.5px] border-primary/10 text-primary/70 dark:text-primary-light/70 rounded-md text-[11px] font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // --- Key priorities (collapsible) ---
  if (type === "priorities_array") {
    return (
      <div id={id} data-testid={id} className="flex flex-col gap-2.5">
        <style>{priorityMarkdownStyles}</style>
        {(data as any[]).map((item: any, index: number) => {
          const isExpanded = expandedPriorities[index] ?? false
          const isCrm = !!item.is_crm_opp
          const rationaleText = briefText(
            item.rationale || item.rationale_string_2 || ""
          )
          const needsToggle = rationaleText.length > 180
          const opportunityId = item.id
          const accountId = item.account_id
          const userId = item.user_id
          const crmOppId = isCrm
            ? String(opportunityId).replace("crm_", "")
            : null
          const link = isCrm
            ? `/crm-pipeline?accountId=${accountId}&oppId=${crmOppId}`
            : `/accounts/${accountId}/priority/${opportunityId}?user_id=${userId}#account-priorities`
          const tags = isCrm
            ? []
            : briefText(item.rationale_string)
                .trim()
                .split(", ")
                .filter(Boolean)

          return (
            <div
              key={index}
              className="rounded-xl border-[0.5px] border-light-border dark:border-border bg-white dark:bg-dark-2 transition-all hover:border-primary/25 dark:hover:border-primary-light/25 hover:shadow-[0_2px_8px_rgba(108,92,231,0.06)] overflow-hidden"
            >
              <div className="flex items-center gap-3 px-4 py-3 border-b border-light-border/50 dark:border-border/50">
                {isCrm ? (
                  <img
                    src={syncIcon}
                    alt="CRM"
                    className="w-4 h-4 object-contain flex-shrink-0"
                  />
                ) : (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{
                      background: "#0984e3",
                      boxShadow: "0 0 0 3px rgba(9,132,227,0.15)"
                    }}
                  />
                )}
                <p className="text-[13px] font-semibold text-dark-2 dark:text-dark-6 flex-1">
                  {briefText(item.name)}
                </p>
              </div>
              <div className="px-4 pt-3 pb-0">
                {isCrm && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2.5 text-[11px]">
                    {item.stage_name && (
                      <span className="px-2 py-0.5 rounded bg-primary/10 text-primary dark:text-primary-light font-medium">
                        {item.stage_name}
                      </span>
                    )}
                    {item.amount != null && (
                      <span className="text-dark-2/70 dark:text-dark-6/70">
                        $
                        {Number(item.amount).toLocaleString(undefined, {
                          maximumFractionDigits: 0
                        })}
                      </span>
                    )}
                    {item.close_date && (
                      <span className="text-dark-2/70 dark:text-dark-6/70">
                        Close:{" "}
                        {new Date(item.close_date).toLocaleDateString("en-US", {
                          day: "numeric",
                          month: "short",
                          year: "numeric"
                        })}
                      </span>
                    )}
                    {item.type && (
                      <span className="text-dark-2/70 dark:text-dark-6/70">
                        {item.type}
                      </span>
                    )}
                    {item.geography && (
                      <span className="text-dark-2/70 dark:text-dark-6/70">
                        {item.geography}
                      </span>
                    )}
                  </div>
                )}
                <div className="relative">
                  <div
                    className="priority-markdown bg-[#fafafa] dark:bg-dark-1/40 rounded-lg p-3.5 overflow-hidden"
                    style={{ maxHeight: isExpanded ? "none" : "72px" }}
                  >
                    <MarkdownRender content={rationaleText} className="!p-0" />
                  </div>
                  {!isExpanded && needsToggle && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#fafafa] dark:from-dark-2 to-transparent rounded-b-lg pointer-events-none" />
                  )}
                </div>
                <div className="flex items-center justify-between py-2.5 gap-2">
                  <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                    {tags.map((tag: string, i: number) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-genesis dark:bg-dark-1/40 border-[0.5px] border-primary/10 text-primary/70 dark:text-primary-light/70 rounded-md text-[11px] font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {needsToggle && (
                      <button
                        className="flex items-center gap-1 text-[11px] text-primary dark:text-primary-light font-medium hover:underline"
                        onClick={() =>
                          setExpandedPriorities((prev) => ({
                            ...prev,
                            [index]: !isExpanded
                          }))
                        }
                      >
                        {isExpanded ? "Less" : "More"}
                        <ChevronDown
                          size={12}
                          className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        track(EVENTS.CLICK_OP_FROM_PRE_MEETING, {
                          ...getBaseEventProps({
                            feature: "pre_meeting_brief",
                            accountId
                          }),
                          opportunity_id: opportunityId,
                          opportunity_name: briefText(item.name)
                        })
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        className="text-[11px] flex items-center gap-1 !text-primary dark:!text-primary-light hover:!bg-primary/5"
                        data-test="priority-more-details"
                      >
                        {t("person_brief.label_more_details")}{" "}
                        <ArrowRightOutlined />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // --- News / posts (rich array horizontal) ---
  if (type === "rich_array_horizontal") {
    const visibleItems = showAllNews ? data : (data as any[]).slice(0, 3)
    return (
      <div id={id} data-testid={id} className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-2.5">
          {(visibleItems as any[]).map(
            (
              item: { title: string; link: string; date: string; type: string },
              index: number
            ) => (
              <a
                key={index}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track(EVENTS.NEWS_CLICKED, {
                    ...getBaseEventProps({ feature: "pre_meeting_brief" }),
                    timestamp: Date.now(),
                    date: item.date,
                    title: briefText(item.title),
                    link: item.link
                  })
                }
                className="group flex flex-col gap-2 p-4 rounded-xl border-[0.5px] border-light-border dark:border-border bg-white dark:bg-dark-2 transition-all hover:border-primary/25 dark:hover:border-primary-light/25 hover:shadow-[0_2px_8px_rgba(108,92,231,0.06)]"
              >
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-text-gray-300 opacity-70">
                  {formatDate(item.date)}
                </p>
                <h2 className="text-13 font-medium text-dark-2 dark:text-dark-6 line-clamp-3 group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
                  {briefText(item.title)}
                </h2>
                <div className="mt-auto flex items-center gap-1 text-xs text-gray-500 dark:text-text-gray-300 opacity-60 group-hover:text-primary dark:group-hover:text-primary-light group-hover:opacity-100 transition-all">
                  <span>Read more</span>
                  <ExternalLink size={10} />
                </div>
              </a>
            )
          )}
        </div>
        {(data as any[]).length > 3 && (
          <button
            className="text-13 text-primary dark:text-primary-light hover:underline text-left"
            onClick={() => setShowAllNews((v) => !v)}
          >
            {showAllNews
              ? "Show less"
              : `Show ${(data as any[]).length - 3} more`}
          </button>
        )}
      </div>
    )
  }

  // --- Communication guidelines ---
  if (type === "heading_table") {
    return (
      <div id={id} data-testid={id} className="grid grid-cols-2 gap-2.5">
        {Object.entries(data).map(([key, value], index) => {
          const cfg = GUIDELINE_CONFIG[key] ?? {
            label: key,
            hex: "#6c5ce7",
            bg: "rgba(108,92,231,0.10)",
            shadow: "0 1px 3px rgba(108,92,231,0.15)"
          }
          return (
            <div
              key={index}
              className="rounded-xl border-[0.5px] border-light-border dark:border-border bg-white dark:bg-dark-2 transition-all hover:border-primary/25 dark:hover:border-primary-light/25 hover:shadow-[0_2px_8px_rgba(108,92,231,0.06)] overflow-hidden"
            >
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-light-border/50 dark:border-border/50">
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: cfg.bg,
                    color: cfg.hex,
                    boxShadow: cfg.shadow
                  }}
                >
                  {getGuidelineIcon(key)}
                </span>
                <p className="text-[13px] font-semibold text-dark-2 dark:text-dark-6">
                  {t(cfg.label)}
                </p>
              </div>
              <div className="p-4">
                {Array.isArray(value) ? (
                  <ul className="flex flex-col gap-1.5">
                    {(value as any[]).map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-13 leading-[1.65] text-dark-2 dark:text-dark-6"
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full mt-[5px] flex-shrink-0"
                          style={{ background: cfg.hex, opacity: 0.45 }}
                        />
                        {briefText(item)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-13 leading-[1.65] text-dark-2 dark:text-dark-6">
                    {briefText(value as unknown)}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // --- Personality traits ---
  if (type === "personality_traits") {
    const traits = Array.isArray(data.key_characteristics)
      ? data.key_characteristics
      : briefText(data.key_characteristics).split(", ").filter(Boolean)

    return (
      <div id={id} data-testid={id} className="flex flex-col gap-3">
        {data.summary && (
          <div className="bg-gradient-to-br from-genesis via-white to-tertiary/20 dark:from-dark-2 dark:via-dark-2 dark:to-[#352F6A]/20 rounded-xl p-4 border-[0.5px] border-primary/10 dark:border-primary/20">
            <MarkdownRender
              content={briefText(data.summary)}
              className="!p-0 text-[13.5px] leading-[1.7]"
            />
          </div>
        )}
        {(traits as unknown[]).length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {(traits as unknown[]).map((trait: unknown, index: number) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-2 px-3 py-2 rounded-lg shadow-[0_1px_2px_rgba(0,0,0,0.06)] border-[0.5px] border-light-border dark:border-border transition-all hover:border-primary/30 hover:shadow-[0_1px_4px_rgba(108,92,231,0.08)]"
              >
                <p className="text-xs text-dark-2 dark:text-dark-6">
                  {briefText(trait)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // --- Skills / competitors ---
  if (type === "competitors") {
    return (
      <div id={id} data-testid={id} className="flex gap-2 flex-wrap">
        {(data as unknown[]).map((item: unknown, index: number) => (
          <div
            key={index}
            className="bg-white dark:bg-dark-2 border-[0.5px] border-light-border dark:border-border rounded-lg shadow-[0_1px_2px_rgba(0,0,0,0.06)] px-3 py-2 transition-all hover:border-primary/30 hover:shadow-[0_1px_4px_rgba(108,92,231,0.08)]"
          >
            <p className="text-xs text-dark-2 dark:text-dark-6">
              {briefText(item)}
            </p>
          </div>
        ))}
      </div>
    )
  }

  // --- Strategic agenda ---
  if (type === "strategic_agenda") {
    const [introLine, ...lines] = (data as string).split("\n")
    const initiatives = lines
      .filter((line) => line.startsWith("- "))
      .map((line) => {
        const parts = line.replace("- ", "").split(": ")
        return { title: parts[0], description: parts.slice(1).join(": ") }
      })
    return (
      <div id={id} data-testid={id} className="flex flex-col gap-3">
        {introLine && (
          <p className="text-13 leading-[1.65] text-dark-2 dark:text-dark-6">
            {introLine}
          </p>
        )}
        <div className="flex flex-col gap-2.5">
          {initiatives.map((initiative, index) => (
            <div
              key={index}
              className="flex gap-3 rounded-xl border-[0.5px] border-light-border dark:border-border bg-white dark:bg-dark-2 p-4 transition-all hover:border-primary/25 dark:hover:border-primary-light/25 hover:shadow-[0_2px_8px_rgba(108,92,231,0.06)]"
            >
              <span className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[11px] font-semibold flex-shrink-0 bg-[#00b894]">
                {index + 1}
              </span>
              <div className="flex flex-col gap-0.5">
                <p className="text-[13px] font-semibold text-dark-2 dark:text-dark-6">
                  {initiative.title?.replace(/\*/g, "")}
                </p>
                {initiative.description && (
                  <p className="text-13 leading-[1.65] text-gray-500 dark:text-text-gray-300">
                    {initiative.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // --- Generic array ---
  if (Array.isArray(data)) {
    return (
      <div id={id} data-testid={id} className="flex flex-col gap-2">
        {data.map((item: any, index: number) => (
          <div
            key={index}
            className="flex items-start gap-2 text-13 leading-[1.65] text-dark-2 dark:text-dark-6"
          >
            <span className="text-primary dark:text-primary-light font-medium text-[11px] mt-0.5 flex-shrink-0">
              {index + 1}.
            </span>
            {briefText(item)}
          </div>
        ))}
      </div>
    )
  }

  // --- Default: markdown text ---
  return (
    <div id={id} data-testid={id}>
      <MarkdownRender
        content={typeof data === "string" ? data : briefText(data)}
        className="!p-0 text-13 leading-[1.65]"
      />
    </div>
  )
}
